/**
 * Engineering Memory API client — consumes existing /api/v2/engineering/patterns endpoints.
 * No backend changes. Unwraps { success, data } envelopes exactly as returned.
 */
import { useQuery } from '@tanstack/react-query';
import { apiV2Envelope } from './api';
import { pollIntervalMs } from '../lib/queryPolling';

/** Session-authenticated `/api/v2` fetch (JWT wins; automation key only as fallback). */
const memFetch = <T,>(path: string) => apiV2Envelope<T>(path);

function q(params: Record<string, string | undefined>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const memoryKeys = {
  patterns: (projectId: string) => ['eng-memory', 'patterns', projectId] as const,
  match: (incidentId: string) => ['eng-memory', 'match', incidentId] as const,
  reuse: (incidentId: string) => ['eng-memory', 'reuse', incidentId] as const,
  decision: (incidentId: string) => ['eng-memory', 'decision', incidentId] as const,
  observation: (incidentId: string) => ['eng-memory', 'observation', incidentId] as const,
  evolution: (patternId: string) => ['eng-memory', 'evolution', patternId] as const,
};

/** Pattern shape as returned by the backend (fields optional — consume as-is). */
export interface EngineeringPatternView {
  pattern_id?: string;
  project_id?: string;
  canonical_fingerprint?: string;
  soft_fingerprint?: string;
  failure_signature?: string;
  root_cause?: string;
  classification?: string;
  repository_id?: string;
  affected_services?: string[];
  affected_files?: string[];
  affected_symbols?: string[];
  confidence?: number;
  success_rate?: number;
  occurrence_count?: number;
  success_count?: number;
  version?: number;
  failure_domain?: string;
  exception_family?: string;
  verification_evidence?: string[];
  replay_evidence?: string[];
  regression_evidence?: string[];
  impact_analysis?: string[];
  deployment_evidence?: string[];
  runtime_observation?: string[];
  engineering_notes?: string[];
  source_episode_ids?: string[];
  confidence_history?: Array<{ at?: string; confidence?: number; reason?: string }>;
  success_history?: Array<{ at?: string; success_rate?: number; reason?: string }>;
  lineage?: string[];
  first_seen?: string;
  last_seen?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface PatternMatchResultView {
  pattern_id?: string;
  overall_score?: number;
  confidence?: number;
  recommendation?: string;
  evidence_explanation?: { reason?: string; matched_count?: number; compared_count?: number };
  pattern?: EngineeringPatternView;
  [key: string]: unknown;
}

export interface MatchBundleView {
  incident_id?: string;
  project_id?: string;
  fingerprint_id?: string;
  canonical_hash?: string;
  top_recommendation?: string;
  candidate_count?: number;
  matches?: PatternMatchResultView[];
  matched_at?: string;
  [key: string]: unknown;
}

export interface ReuseStageView {
  stage?: string;
  status?: string;
  mode?: string;
  reason?: string;
  evidence?: string[];
  critical?: boolean;
  [key: string]: unknown;
}

export interface ReuseReportView {
  report_id?: string;
  pattern_id?: string;
  overall_verdict?: string;
  confidence?: number;
  live_validation_ok?: boolean;
  historical_success?: boolean;
  match_score?: number;
  match_recommendation?: string;
  stages?: ReuseStageView[];
  replay?: ReuseStageView;
  verification?: ReuseStageView;
  regression?: ReuseStageView;
  impact?: ReuseStageView;
  deployment?: ReuseStageView;
  reasons?: string[];
  recommendations?: string[];
  evidence?: string[];
  pattern?: EngineeringPatternView;
  [key: string]: unknown;
}

export interface EngineeringDecisionView {
  decision_id?: string;
  project_id?: string;
  incident_id?: string;
  execution_mode?: string;
  memory_hit?: boolean;
  matched_pattern_id?: string;
  match_score?: number;
  match_recommendation?: string;
  root_cause_verdict?: string;
  validation_verdict?: string;
  pattern_confidence?: number;
  pattern_success_rate?: number;
  reasons?: string[];
  fallback_reason?: string;
  policy_checks?: Array<{ id?: string; passed?: boolean; detail?: string; required?: boolean; value?: string }>;
  decided_at?: string;
  pattern?: EngineeringPatternView;
  validation_report?: ReuseReportView;
  [key: string]: unknown;
}

export interface ObservationTickView {
  window_id?: string;
  status?: string;
  verdict?: string;
  reason?: string;
  evidence?: string[];
  observed_at?: string;
  [key: string]: unknown;
}

export interface ObservationJobView {
  job_id?: string;
  incident_id?: string;
  pattern_id?: string;
  status?: string;
  overall_verdict?: string;
  ticks?: ObservationTickView[];
  timeline?: Array<{ at?: string; event?: string; detail?: string; verdict?: string }>;
  scheduled_at?: string;
  completed_at?: string;
  [key: string]: unknown;
}

export interface ObservationPayloadView {
  status?: ObservationJobView | null;
  timeline?: Array<{ at?: string; event?: string; detail?: string; verdict?: string }>;
  report?: {
    overall_verdict?: string;
    reasons?: string[];
    recommendations?: string[];
    pattern_evolution?: {
      pattern_id?: string;
      from_version?: number;
      to_version?: number;
      prior_confidence?: number;
      new_confidence?: number;
      verdict?: string;
    };
    [key: string]: unknown;
  } | null;
}

export function useEngineeringPatterns(projectId: string, limit = 40) {
  return useQuery({
    queryKey: memoryKeys.patterns(projectId),
    queryFn: () =>
      memFetch<{ patterns: EngineeringPatternView[]; count: number }>(
        `/engineering/patterns${q({ project_id: projectId, limit: String(limit) })}`,
      ),
    enabled: Boolean(projectId),
    refetchInterval: () => pollIntervalMs(30_000),
  });
}

export function usePatternMatch(incidentId: string | null | undefined) {
  return useQuery({
    queryKey: memoryKeys.match(incidentId || ''),
    queryFn: () =>
      memFetch<MatchBundleView>(`/engineering/patterns/match${q({ incidentId: incidentId! })}`),
    enabled: Boolean(incidentId),
    refetchInterval: () => pollIntervalMs(20_000),
  });
}

export function useReuseValidation(incidentId: string | null | undefined) {
  return useQuery({
    queryKey: memoryKeys.reuse(incidentId || ''),
    queryFn: () =>
      memFetch<{ reports: ReuseReportView[]; count: number }>(
        `/engineering/patterns/reuse-validation${q({ incidentId: incidentId! })}`,
      ),
    enabled: Boolean(incidentId),
    refetchInterval: () => pollIntervalMs(20_000),
  });
}

export function useEngineeringMemoryDecision(incidentId: string | null | undefined) {
  return useQuery({
    queryKey: memoryKeys.decision(incidentId || ''),
    queryFn: () =>
      memFetch<EngineeringDecisionView | null>(
        `/engineering/patterns/decision${q({ incidentId: incidentId! })}`,
      ),
    enabled: Boolean(incidentId),
    refetchInterval: () => pollIntervalMs(20_000),
  });
}

export function useObservationStatus(incidentId: string | null | undefined) {
  return useQuery({
    queryKey: memoryKeys.observation(incidentId || ''),
    queryFn: () =>
      memFetch<ObservationPayloadView>(
        `/engineering/patterns/observation${q({ incidentId: incidentId! })}`,
      ),
    enabled: Boolean(incidentId),
    refetchInterval: () => pollIntervalMs(15_000),
  });
}

export function usePatternEvolution(patternId: string | null | undefined) {
  return useQuery({
    queryKey: memoryKeys.evolution(patternId || ''),
    queryFn: () => memFetch<Record<string, unknown>>(`/engineering/patterns/${patternId}/evolution`),
    enabled: Boolean(patternId),
  });
}
